/**
 * <shipping-zone-selector>
 *
 * A flat, type-to-search country dropdown for the International Shipping
 * section. Picking (or typing then clicking) a country reveals that country's
 * shipping zone detail block and hides the rest.
 *
 * Dependency-free custom element. Modeled on the show/hide pattern in
 * assets/country-selector.js but with its own searchable-dropdown logic.
 * Progressive enhancement: all zone blocks are rendered server-side, so the
 * data is present even before this script upgrades the element.
 */
class ShippingZoneSelector extends HTMLElement {
  connectedCallback() {
    this._dropdown = this.querySelector('.sz-dropdown');
    this._trigger = this.querySelector('.sz-trigger');
    this._search = this.querySelector('.sz-search-input');
    this._options = Array.from(this.querySelectorAll('.sz-country-option'));
    this._noResults = this.querySelector('.sz-no-results');
    this._triggerLabel = this.querySelector('.sz-trigger-label');

    if (!this._trigger || !this._dropdown) return;

    // Section scope so multiple instances / other sections don't collide.
    // .trust-tab-content is the PDP delivery drawer, which injects this
    // section's inner markup without the .shipping-section-content wrapper.
    this._scope =
      this.closest('.shipping-section-content') ||
      this.closest('.trust-tab-content') ||
      this;

    this._boundDocClick = this._onDocumentClick.bind(this);

    this._trigger.addEventListener('click', (e) => {
      e.stopPropagation();
      this._toggle();
    });

    if (this._search) {
      this._search.addEventListener('input', () => this._filter());
      // Keep clicks inside the search field from closing the dropdown.
      this._search.addEventListener('click', (e) => e.stopPropagation());
    }

    this._options.forEach((opt) => {
      opt.addEventListener('click', (e) => {
        e.stopPropagation();
        this._select(opt);
      });
    });

    document.addEventListener('click', this._boundDocClick);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this._boundDocClick);
  }

  _toggle() {
    const willOpen = !this._dropdown.classList.contains('is-open');
    this._dropdown.classList.toggle('is-open', willOpen);
    this._trigger.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
    if (willOpen && this._search) {
      this._search.focus();
    }
  }

  _close() {
    this._dropdown.classList.remove('is-open');
    this._trigger.setAttribute('aria-expanded', 'false');
  }

  _onDocumentClick(e) {
    if (!this.contains(e.target)) {
      this._close();
    }
  }

  _filter() {
    const term = this._search.value.trim().toLowerCase();
    let visibleCount = 0;

    this._options.forEach((opt) => {
      const name = (opt.dataset.name || '').toLowerCase();
      const match = term === '' || name.indexOf(term) !== -1;
      opt.classList.toggle('hidden', !match);
      if (match) visibleCount += 1;
    });

    if (this._noResults) {
      this._noResults.classList.toggle('hidden', visibleCount !== 0);
    }
  }

  _select(opt) {
    const zone = opt.dataset.zone;
    const name = opt.dataset.name || '';

    if (this._triggerLabel) {
      // Keep the flag + name in the trigger after selection.
      this._triggerLabel.innerHTML = opt.innerHTML;
    }
    this._trigger.classList.add('is-selected');

    // Move the dropdown highlight to the chosen option (it starts on the
    // server pre-selected country; keep it in sync on manual selection).
    this._options.forEach((o) => {
      const isActive = o === opt;
      o.classList.toggle('is-active', isActive);
      if (isActive) {
        o.setAttribute('aria-selected', 'true');
      } else {
        o.removeAttribute('aria-selected');
      }
    });

    this._scope.querySelectorAll('.sz-zone-block').forEach((block) => {
      block.classList.toggle('hidden', block.dataset.zone !== zone);
    });

    this._close();
  }
}

if (!customElements.get('shipping-zone-selector')) {
  customElements.define('shipping-zone-selector', ShippingZoneSelector);
}
